import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Bell, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { PUSH_BUTTON } from '@/lib/pushButtonStyles';
import { translateServerError } from '@/lib/serverErrors';
import {
  describeState, disablePush, enablePush, fetchConfig, getDeviceId, getPermission, listDevices, sendTest, setPreferences, unsupportedReason,
} from '@/lib/push';

const CATEGORIES = ['new_booking', 'cancellation', 'message'];

/** Accessible button: explicit Ridalo colours (see lib/pushButtonStyles.js), spinner while its own action runs. */
function ActionButton({ variant = 'primary', loading = false, children, ...props }) {
  return (
    <button type="button" className={PUSH_BUTTON[variant]} aria-busy={loading || undefined} {...props}>
      {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
      <span>{children}</span>
    </button>
  );
}

/** Account settings: « Notifications sur cet appareil ». Nothing is requested until the
 *  person presses « Activer » on this very device. E-mails and in-app notifications are
 *  not affected by anything done here. */
export function PushSettingsCard() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const lang = (i18n.resolvedLanguage || i18n.language || 'fr').startsWith('en') ? 'en' : 'fr';
  const [config, setConfig] = useState(null);
  const [device, setDevice] = useState(null);
  const [permission, setPermission] = useState(getPermission());
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);       // which action is running (spinner on that button)
  const unsupported = unsupportedReason();

  const load = useCallback(async () => {
    setPermission(getPermission());
    if (unsupported) { setConfig({ available: false }); return; }
    try {
      const cfg = await fetchConfig();
      setConfig(cfg);
      if (cfg.available) {
        const id = getDeviceId();
        setDevice((await listDevices()).find((d) => d.device_id === id) || null);
      } else setDevice(null);
    } catch { setConfig({ available: false, reason: 'offline' }); }
  }, [unsupported]);
  useEffect(() => { load(); }, [load]);

  const state = describeState({ unsupported, config, permission, device });
  const run = async (fn, name = null) => {
    setBusy(true);
    setPending(name);
    try { await fn(); } catch (e) {
      toast.error(translateServerError(e?.response?.data?.detail) || t('pushSettings.error'));
    } finally { setBusy(false); setPending(null); await load(); }
  };

  const enable = () => run(async () => {
    const r = await enablePush({ language: lang, userId: user?.user_id });
    if (r.status === 'enabled') toast.success(t('pushSettings.enabledToast'));
    else if (r.status === 'denied') toast.error(t('pushSettings.deniedToast'));
  }, 'enable');
  const disable = () => run(async () => { await disablePush(); toast.success(t('pushSettings.disabledToast')); }, 'disable');
  const test = () => run(async () => {
    try { await sendTest(lang); toast.success(t('pushSettings.testSent')); } catch (e) {
      const code = e?.response?.data?.detail;
      toast.error(code === 'too_fast' ? t('pushSettings.testTooFast') : code === 'subscription_expired' ? t('pushSettings.testExpired') : t('pushSettings.error'));
    }
  }, 'test');
  const toggle = (category, value) => {
    setDevice((d) => ({ ...d, prefs: { ...d.prefs, [category]: value } }));      // immediate feedback; reloaded from the server afterwards
    return run(async () => { await setPreferences(device.device_id, { [category]: value }); });
  };

  const reasonKey = state === 'unavailable' ? `pushSettings.unavailable.${['disabled', 'not_allowed', 'not_configured', 'offline'].includes(config?.reason) ? config.reason : 'disabled'}` : null;

  return (
    <div className="card-dashboard" data-testid="push-settings-card" data-state={state}>
      <div className="flex items-center gap-2 mb-1">
        <Bell className="w-4 h-4 text-accent" />
        <h3 className="font-semibold text-primary">{t('pushSettings.title')}</h3>
      </div>
      <p className="text-xs text-ink-light mb-3">{t('pushSettings.intro')}</p>

      {state === 'loading' && <p className="text-sm text-ink-light">{t('pushSettings.loading')}</p>}
      {state === 'ios_install' && <p className="text-sm" data-testid="push-ios-install">{t('pushSettings.iosInstall')}</p>}
      {state === 'unsupported' && <p className="text-sm" data-testid="push-unsupported">{t('pushSettings.unsupported')}</p>}
      {state === 'unavailable' && <p className="text-sm" data-testid="push-unavailable">{t(reasonKey)}</p>}
      {state === 'denied' && <p className="text-sm" data-testid="push-denied">{t('pushSettings.denied')}</p>}

      {state === 'off' && (
        <div>
          <p className="text-sm text-ink-light mb-3" data-testid="push-status">{t('pushSettings.statusOff')}</p>
          <ActionButton onClick={enable} disabled={busy} loading={pending === 'enable'} data-testid="push-enable">{t('pushSettings.enable')}</ActionButton>
        </div>
      )}

      {state === 'on' && device && (
        <div>
          <p className="text-sm mb-3" data-testid="push-status">{t('pushSettings.statusOn')}</p>
          <div className="space-y-2 mb-3">
            {CATEGORIES.map((c) => (
              <label key={c} className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="h-4 w-4 accent-primary" checked={device.prefs?.[c] !== false} disabled={busy} onChange={(e) => toggle(c, e.target.checked)} data-testid={`push-pref-${c}`} />
                {t(`pushSettings.categories.${c}`)}
              </label>
            ))}
          </div>
          <p className="text-xs text-ink-light mb-3">{t('pushSettings.privacy')}</p>
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
            <ActionButton variant="secondary" onClick={test} disabled={busy} loading={pending === 'test'} data-testid="push-test">{t('pushSettings.test')}</ActionButton>
            <ActionButton variant="secondary" onClick={disable} disabled={busy} loading={pending === 'disable'} data-testid="push-disable">{t('pushSettings.disable')}</ActionButton>
          </div>
        </div>
      )}
    </div>
  );
}
